/**
 * Dashboard API Client
 *
 * Provides dashboard registry and layout endpoints.
 * Originally split from platform.ts (Phase 3).
 */

import client from './client';
import type { SettingsField } from './settings-api';

const api = {
  get: <T = unknown>(path: string, config?: Record<string, unknown>) =>
    client.get<T>(`/api/platform${path}`, config),
  post: <T = unknown>(path: string, data?: unknown, config?: Record<string, unknown>) =>
    client.post<T>(`/api/platform${path}`, data, config),
  put: <T = unknown>(path: string, data?: unknown, config?: Record<string, unknown>) =>
    client.put<T>(`/api/platform${path}`, data, config),
  delete: <T = unknown>(path: string, config?: Record<string, unknown>) =>
    client.delete<T>(`/api/platform${path}`, config),
};

// ============================================================
// DASHBOARD INTERFACES
// ============================================================

export interface PanelSize {
  w: number;
  h: number;
}

export interface PanelPosition {
  x: number;
  y: number;
}

export interface DashboardPanel {
  id: string;
  pluginId: string;
  title: string;
  icon?: string;
  description?: string;
  category?: string;
  defaultSize?: PanelSize;
  minSize?: PanelSize;
  maxSize?: PanelSize;
  requiredPermissions?: string[];
  componentUrl?: string;
  resizable?: boolean;
  removable?: boolean;
  configFields?: SettingsField[];
  refreshInterval?: number;
  enabled: boolean;
  registeredAt: string;
}

export interface DashboardPanelInstance {
  id: string;
  panelId: string;
  pluginId: string;
  userId: string;
  position: PanelPosition;
  size: PanelSize;
  config: Record<string, unknown>;
  visible: boolean;
  order: number;
}

export interface DashboardLayout {
  userId: string;
  name?: string;
  panels: DashboardPanelInstance[];
  columns: number;
  rowHeight: number;
  compactType?: 'vertical' | 'horizontal' | null;
  updatedAt: string;
}

export interface DashboardPanelsData {
  panels: DashboardPanel[];
  byCategory: Record<string, DashboardPanel[]>;
}

// ============================================================
// DASHBOARD FUNCTIONS
// ============================================================

export async function getDashboardPanels(): Promise<DashboardPanelsData> {
  const response = await api.get<DashboardPanelsData>('/dashboard/panels');
  return response.data;
}

export async function getDashboardLayout(): Promise<DashboardLayout> {
  const response = await api.get<DashboardLayout>('/dashboard/layout');
  return response.data;
}

export async function saveDashboardLayout(
  layout: Partial<DashboardLayout>,
): Promise<{ success: boolean }> {
  const response = await api.put<{ success: boolean }>('/dashboard/layout', layout);
  return response.data;
}

export async function resetDashboardLayout(): Promise<DashboardLayout> {
  const response = await api.post<DashboardLayout>('/dashboard/layout/reset');
  return response.data;
}

export async function addDashboardPanel(
  panelId: string,
  config?: Record<string, unknown>,
): Promise<DashboardPanelInstance> {
  const response = await api.post<DashboardPanelInstance>('/dashboard/panels/add', { panelId, config });
  return response.data;
}

export async function updateDashboardPanel(
  instanceId: string,
  updates: Partial<DashboardPanelInstance>,
): Promise<{ success: boolean }> {
  const response = await api.put<{ success: boolean }>(`/dashboard/panels/${instanceId}`, updates);
  return response.data;
}

export async function removeDashboardPanel(instanceId: string): Promise<void> {
  await api.delete(`/dashboard/panels/${instanceId}`);
}
